/**
 * A member's reply in two parts: the plain answer, then the technical details behind it (ids, tools, how a run went).
 * The member writes the details last, under a line that starts with the marker; the thread shows the answer and folds
 * the details away under a "Details" toggle (ZhChatThread, ZhChatDetails). Exported from the package so whatever
 * writes replies writes to the same marker the thread reads.
 */
import { closesFence, type MarkdownFence, openFence } from './markdown'

/** The fixed marker a reply's technical section starts with, at the start of its own line. */
export const CHAT_DETAILS_MARKER = 'Details:'

export interface ChatDetailsSplit {
    answer: string
    /** Null when the reply has no Details section. */
    details: string | null
}

/**
 * The marker with whatever follows it on its line: `Details:`, case-insensitive, bolded (`**Details:**`,
 * `**Details**:`) or written as a heading (`## Details:`). The rest of that line is the first of the details.
 */
const MARKER = /^ {0,3}(?:#{1,6}[ \t]+)?(\*\*|__)?details(?::\1|\1:)(.*)$/i
/** A heading may leave the colon off: `## Details`, alone on its line. */
const HEADING_MARKER = /^ {0,3}#{1,6}[ \t]+(\*\*|__)?details\1[ \t]*#*[ \t]*$/i

/** What follows the marker on a line that starts with it, else null. */
function afterMarker(line: string): string | null {
    if (HEADING_MARKER.test(line)) {
        return ''
    }
    const marker = MARKER.exec(line)
    return marker ? (marker[2] ?? '') : null
}

/**
 * Splits a reply at the first line that starts with the marker, outside a fenced code block. Everything after it is
 * the details, later markers included. A reply with no marker comes back whole, with null details.
 */
export function splitChatDetails(text: string): ChatDetailsSplit {
    const lines = text.split(/\r\n?|\n/)
    let fence: MarkdownFence | null = null
    for (const [index, line] of lines.entries()) {
        if (fence) {
            fence = closesFence(line, fence) ? null : fence
            continue
        }
        fence = openFence(line)
        const rest = fence ? null : afterMarker(line)
        if (rest !== null) {
            const details = [rest.trim(), ...lines.slice(index + 1)]
                .join('\n')
                .replace(/^\s*\n/, '')
                .trimEnd()
            return { answer: lines.slice(0, index).join('\n').trimEnd(), details: details || null }
        }
    }
    return { answer: text, details: null }
}
